<script lang="ts">
  import { api } from '$shared/api';
  import { onActivityUpdate } from '$lib/stores/socket.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import Avatar from '$shared/Avatar.svelte';

  interface ActivityEntry {
    id: number;
    type: string;
    description: string;
    duel_id: number | null;
    duel_name: string | null;
    track_id: number | null;
    track_name: string | null;
    target_username: string | null;
    target_avatar_url: string | null;
    top_vote: boolean;
    created_at: string;
  }

  type FilterType = 'all' | 'vote' | 'listen' | 'follow';

  let activities = $state<ActivityEntry[]>([]);
  let loading = $state(true);
  let activeFilter = $state<FilterType>('all');
  let hasNewActivity = $state(false);

  const filteredActivities = $derived(
    activeFilter === 'all' ? activities : activities.filter((a) => a.type === activeFilter),
  );

  const groupedActivities = $derived(groupByDate(filteredActivities));

  const filterCounts = $derived({
    all: activities.length,
    vote: activities.filter((a) => a.type === 'vote').length,
    listen: activities.filter((a) => a.type === 'listen').length,
    follow: activities.filter((a) => a.type === 'follow').length,
  });

  function fetchActivity() {
    loading = true;
    api
      .get<{ activity: ActivityEntry[] }>('/users/current/activity')
      .then((d) => {
        activities = d.activity || [];
        loading = false;
        hasNewActivity = false;
      })
      .catch((e: unknown) => {
        console.error('Failed to load activity:', e);
        loading = false;
      });
  }

  $effect(() => {
    fetchActivity();

    onActivityUpdate(() => {
      hasNewActivity = true;
    });

    return () => {
      onActivityUpdate(null);
    };
  });

  function formatRelativeTime(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffSec = Math.floor((now - then) / 1000);

    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function getDateGroupLabel(dateStr: string): string {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function groupByDate(entries: ActivityEntry[]): { label: string; items: ActivityEntry[] }[] {
    const groups: Map<string, ActivityEntry[]> = new Map();
    for (const entry of entries) {
      const label = getDateGroupLabel(entry.created_at);
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label)!.push(entry);
    }
    return Array.from(groups, ([label, items]) => ({ label, items }));
  }

  function activityIcon(type: string, topVote: boolean): string {
    switch (type) {
      case 'vote':
        return topVote ? '⭐' : '🎵';
      case 'listen':
        return '🎧';
      case 'follow':
        return '👤';
      case 'submit':
        return '📤';
      case 'join':
        return '⚔️';
      default:
        return '•';
    }
  }

  function emptyMessage(filter: FilterType): string {
    switch (filter) {
      case 'vote':
        return 'No votes yet — start voting in a duel!';
      case 'listen':
        return 'No listens yet — discover tracks in a duel!';
      case 'follow':
        return 'No follows yet — find players to follow!';
      default:
        return 'No activity yet. Start by joining a duel!';
    }
  }

  function emptyLink(filter: FilterType): { href: string; text: string } | null {
    switch (filter) {
      case 'vote':
        return { href: '#/duels', text: 'Browse duels' };
      case 'listen':
        return { href: '#/duels', text: 'Browse duels' };
      case 'follow':
        return { href: '#/leaderboard', text: 'Find players' };
      case 'all':
        return { href: '#/duels', text: 'Browse duels' };
      default:
        return null;
    }
  }

  const filters: { key: FilterType; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'vote', label: 'Votes' },
    { key: 'listen', label: 'Listens' },
    { key: 'follow', label: 'Follows' },
  ];
</script>

<div class="activity-page">
  <h1>Activity</h1>

  <!-- Filter tabs -->
  <div class="filter-tabs">
    {#each filters as f}
      <button class="filter-tab" class:active={activeFilter === f.key} onclick={() => (activeFilter = f.key)}>
        {f.label}
        {#if filterCounts[f.key] > 0}
          <span class="badge">{filterCounts[f.key]}</span>
        {/if}
      </button>
    {/each}
  </div>

  <!-- New activity banner -->
  {#if hasNewActivity}
    <button class="new-activity-banner" onclick={fetchActivity}> New activity available — click to refresh </button>
  {/if}

  {#if loading}
    <LoadingSkeleton lines={8} />
  {:else if filteredActivities.length === 0}
    {@const link = emptyLink(activeFilter)}
    <div class="empty-state">
      <div class="empty-icon">
        {#if activeFilter === 'vote'}🎵
        {:else if activeFilter === 'listen'}🎧
        {:else if activeFilter === 'follow'}👤
        {:else}📋
        {/if}
      </div>
      <p class="empty-text">{emptyMessage(activeFilter)}</p>
      {#if link}
        <a href={link.href} class="btn btn-primary btn-sm">{link.text}</a>
      {/if}
    </div>
  {:else}
    <div class="activity-list">
      {#each groupedActivities as group}
        <div class="date-group">
          <div class="date-header">{group.label}</div>
          {#each group.items as entry}
            <div class="activity-row">
              <div class="activity-avatar">
                {#if entry.target_username}
                  <a href="#/users/{entry.target_username}">
                    <Avatar username={entry.target_username} avatarUrl={entry.target_avatar_url} size="feed" />
                  </a>
                {:else}
                  <span class="activity-icon-circle">{activityIcon(entry.type, entry.top_vote)}</span>
                {/if}
              </div>
              <div class="activity-content">
                <div class="activity-desc">
                  {#if entry.type === 'vote' || entry.type === 'listen'}
                    <span class="activity-icon-inline">{activityIcon(entry.type, entry.top_vote)}</span>
                    {#if entry.type === 'vote'}
                      {entry.top_vote ? 'Top voted' : 'Voted on'}
                      {#if entry.target_username}
                        <a href="#/users/{entry.target_username}" class="actor-link">{entry.target_username}</a>'s track
                      {:else}
                        a track
                      {/if}
                      {#if entry.duel_name}
                        in <a href="#/duels/{entry.duel_id}" class="duel-link">{entry.duel_name}</a>
                      {/if}
                    {:else}
                      Listened to
                      {#if entry.target_username}
                        <a href="#/users/{entry.target_username}" class="actor-link">{entry.target_username}</a>'s track
                      {:else}
                        a track
                      {/if}
                      {#if entry.duel_name}
                        in <a href="#/duels/{entry.duel_id}" class="duel-link">{entry.duel_name}</a>
                      {/if}
                    {/if}
                  {:else if entry.type === 'follow'}
                    <span class="activity-icon-inline">{activityIcon(entry.type, false)}</span>
                    Followed <a href="#/users/{entry.target_username}" class="actor-link">{entry.target_username}</a>
                  {:else}
                    <span class="activity-icon-inline">{activityIcon(entry.type, false)}</span>
                    {entry.description}
                  {/if}
                </div>
                <span class="activity-time">{formatRelativeTime(entry.created_at)}</span>
              </div>
            </div>
          {/each}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .activity-page {
    max-width: 40rem;
    margin: 0 auto;
  }
  h1 {
    margin-bottom: 1.25rem;
  }

  /* Filter tabs */
  .filter-tabs {
    display: flex;
    gap: 0.25rem;
    margin-bottom: 1.25rem;
    border-bottom: 1px solid var(--border);
    padding-bottom: 0;
  }
  .filter-tab {
    background: none;
    border: none;
    color: var(--text-muted);
    font-size: 0.875rem;
    font-weight: 500;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }
  .filter-tab:hover {
    color: var(--text);
  }
  .filter-tab.active {
    color: var(--cyan);
    border-bottom-color: var(--cyan);
  }
  .badge {
    font-size: 0.7rem;
    font-weight: 600;
    background: var(--surface-2);
    color: var(--text-secondary);
    border-radius: 10px;
    padding: 0.1rem 0.4rem;
    min-width: 1.25rem;
    text-align: center;
  }
  .filter-tab.active .badge {
    background: rgba(0, 229, 255, 0.15);
    color: var(--cyan);
  }

  /* New activity banner */
  .new-activity-banner {
    width: 100%;
    background: rgba(0, 229, 255, 0.1);
    border: 1px solid rgba(0, 229, 255, 0.3);
    border-radius: var(--radius);
    color: var(--cyan);
    font-size: 0.8125rem;
    font-weight: 500;
    padding: 0.5rem 1rem;
    cursor: pointer;
    margin-bottom: 1rem;
    text-align: center;
  }
  .new-activity-banner:hover {
    background: rgba(0, 229, 255, 0.15);
    border-color: var(--cyan);
  }

  /* Date groups */
  .date-group {
    margin-bottom: 0.5rem;
  }
  .date-header {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.75rem 0.5rem 0.375rem;
  }

  /* Activity list */
  .activity-list {
    display: flex;
    flex-direction: column;
  }
  .activity-row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.625rem 0.5rem;
    border-bottom: 1px solid var(--border);
    transition: background-color 0.15s ease;
  }
  .activity-row:hover {
    background: var(--surface);
  }
  .activity-row:last-child {
    border-bottom: none;
  }

  /* Avatar column */
  .activity-avatar {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
  }
  .activity-icon-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--surface-2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
  }

  /* Content */
  .activity-content {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
    padding-top: 0.25rem;
  }
  .activity-desc {
    font-size: 0.875rem;
    color: var(--text);
    line-height: 1.4;
  }
  .activity-icon-inline {
    font-size: 0.8rem;
    margin-right: 0.25rem;
  }
  .actor-link {
    color: var(--text);
    font-weight: 600;
  }
  .actor-link:hover {
    color: var(--cyan);
  }
  .duel-link {
    color: var(--cyan);
    font-weight: 500;
  }
  .duel-link:hover {
    color: var(--pink);
  }
  .activity-time {
    font-size: 0.75rem;
    color: var(--text-muted);
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* Empty state */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 3rem 1rem;
    text-align: center;
  }
  .empty-icon {
    font-size: 2.5rem;
  }
  .empty-text {
    color: var(--text-secondary);
    font-size: 0.9375rem;
    max-width: 24rem;
  }

  /* Responsive */
  @media (max-width: 480px) {
    .filter-tab {
      padding: 0.5rem;
      font-size: 0.8125rem;
    }
    .activity-content {
      flex-direction: column;
      gap: 0.125rem;
    }
    .activity-time {
      padding-top: 0;
    }
  }
</style>
